import { memo } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AttendeeNote } from "../../types/Types";
import { NOTE_ACTION_BTN_STYLE } from "./attendeeNotesShared";

type AttendeeNoteViewModalProps = {
  note: AttendeeNote | null;
  selfObjectId?: string;
  onHide: () => void;
  onEdit: (note: AttendeeNote) => void;
};

function AttendeeNoteViewModal({
  note,
  selfObjectId,
  onHide,
  onEdit,
}: AttendeeNoteViewModalProps) {
  const { t } = useTranslation();

  return (
    <Modal show={!!note} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("attendeeNotes.viewTitle")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {note ? (
          <>
            <p className="mb-1">
              <strong>
                {selfObjectId && note.attendeeObjectId === selfObjectId
                  ? t("attendeeNotes.you")
                  : `${note.attendeeFirstName} ${note.attendeeLastName}`.trim()}
              </strong>
            </p>
            <p className="text-muted small mb-3">
              {t("attendeeNotes.personalBadge")}
            </p>
            <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap" }}>
              {note.noteText}
            </p>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="d-flex gap-2 justify-content-end">
        <Button
          variant="outline-secondary"
          style={NOTE_ACTION_BTN_STYLE}
          onClick={onHide}
        >
          {t("attendeeNotes.cancel")}
        </Button>
        <Button
          variant="primary"
          style={NOTE_ACTION_BTN_STYLE}
          onClick={() => note && onEdit(note)}
        >
          {t("attendeeNotes.edit")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

type AttendeeNoteEditModalProps = {
  note: AttendeeNote | null;
  selfObjectId?: string;
  editText: string;
  onEditTextChange: (value: string) => void;
  saving: boolean;
  onHide: () => void;
  onSave: () => void;
};

function AttendeeNoteEditModal({
  note,
  selfObjectId,
  editText,
  onEditTextChange,
  saving,
  onHide,
  onSave,
}: AttendeeNoteEditModalProps) {
  const { t } = useTranslation();

  return (
    <Modal show={!!note} onHide={() => !saving && onHide()} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{t("attendeeNotes.editTitle")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {note ? (
          <>
            <p className="mb-1">
              <strong>
                {selfObjectId && note.attendeeObjectId === selfObjectId
                  ? t("attendeeNotes.you")
                  : `${note.attendeeFirstName} ${note.attendeeLastName}`.trim()}
              </strong>
            </p>
            {note.eventId > 0 && note.eventName ? (
              <p className="text-muted small mb-3">{note.eventName}</p>
            ) : (
              <p className="text-muted small mb-3">
                {t("attendeeNotes.personalBadge")}
              </p>
            )}
            <Form.Control
              as="textarea"
              rows={5}
              maxLength={4000}
              value={editText}
              disabled={saving}
              onChange={(e) => onEditTextChange(e.target.value)}
            />
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="d-flex gap-2 justify-content-end">
        <Button
          variant="outline-secondary"
          style={NOTE_ACTION_BTN_STYLE}
          disabled={saving}
          onClick={onHide}
        >
          {t("attendeeNotes.cancel")}
        </Button>
        <Button
          variant="primary"
          style={NOTE_ACTION_BTN_STYLE}
          disabled={saving || !editText.trim()}
          onClick={onSave}
        >
          {saving ? t("attendeeNotes.saving") : t("attendeeNotes.save")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export const AttendeeNoteViewModalMemo = memo(AttendeeNoteViewModal);
export const AttendeeNoteEditModalMemo = memo(AttendeeNoteEditModal);
